import React from 'react';
import ReactCSSTransition from 'react-transition-group/CSSTransition';
import ButtonAction from './ButtonAction';
import HelpPopover from './HelpPopover';
import _ from 'lodash';
import ElementCountList from './sample/ElementCountList';
import SortableElements from './sample/SortableElements';
import * as actionCreators from '../action_creators';
import SampleConfiguration from '../helpers/SampleConfiguration';
import CONVERSION from '../constants/conversions';
import { getFileTypeName, descriptionForFileType } from '../helpers/FileHelpers';
import { arrayMove } from 'react-sortable-hoc';
import { Container, Row, Col, Table, ButtonGroup, Button, Collapse } from 'reactstrap';
import { connect } from 'react-redux';
import { toJS } from '../helpers/toJS';
import values from 'object.values';

if (!Object.values) {
	values.shim();
}

const availableConversions = CONVERSION.filter(c => c.input === 'sam');
const disallowedCharStyles = ['enref', 'fnref', 'ennum', 'fnnum', 'img'];
const disallowedParStyles = ['en', 'fn', 'structure'];

// currently manages its own state like a redux store; might be worth breaking
// out into a real store eventually
export class Sample extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      elements: {},
      sortableList: [],
      characterStyles: [],
      specialCharacters: [],
      locked: true,
      showSample: false,
    };
  }

  componentDidMount() {
    const originalElements = this.listableElements(this.props.statistics.elements);
    const elements = this.elementObject(originalElements);
    const sortableList = originalElements.map(e => {
      return {name: e.name, weight: e.indexes[0]}
    }).sort((e,f) => e.weight - f.weight);
    const characterStyles = this.usableCharacterStyles(this.props.statistics.elements);
    const specialCharacters = this.props.statistics
      .special_characters
      .map(c => c.character);
    this.setState({
      elements,
      sortableList,
      characterStyles,
      specialCharacters
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.files.length < this.props.files.length && prevProps.loading && !this.props.loading) {
      this.setState({ showSample: true });
    }
  }

  // this returns the display list of elements that users will interact with
  // ignore fn, en, structure
  listableElements(elements) {
    return elements.filter((e) => {
      return e.type == 'para' && !disallowedParStyles.includes(e.name) || e.name == 'table'
    });
  }

  // ignore enref, fnref, img, ennum, fnnum
  usableCharacterStyles(elements) {
    return elements.filter((e) => {
      return e.type == 'char' && !disallowedCharStyles.includes(e.name)
    }).map(e => e.name);
  }

  // this is an object for tracking element characteristics and passing data to
  // children
  elementObject(elements) {
    return elements.reduce((newElements, element) => {
      newElements[element.name] = {
        name: element.name,
        max: element.count,
        indexes: element.indexes,
        count: 1,
      }
      return newElements;
    }, {});
  }

  // wraps any transformations to element object and list to ensure clean updates
  resetState = (callback) => {
    const elements = Object.assign({}, this.state.elements)
    const sortableList = this.state.sortableList.slice();
    callback(elements, sortableList);
    this.setState({ elements, sortableList });
  }

  updateElementCount = (element, count) => {
    this.resetState((elements, sortableList) => {
      this.manageElementCount(elements, sortableList, element, count);
    });
  }

  setAllElementCounts = (count) => {
    this.resetState((elements, sortableList) => {
      for (let element in elements) {
        this.manageElementCount(elements, sortableList, element, count);
      }
    });
  }

  selectAllByStyle = (styleList) => {
    this.resetState((elements, sortableList) => {
      for (let element in elements) {
        if (styleList.includes(element)){
          this.manageElementCount(elements, sortableList, element, elements[element].max);
        }
        else {
          this.manageElementCount(elements, sortableList, element, 0);
        }
      }
    });
  }

  manageElementCount(elementsList, sortedList, element, count) {
    if (elementsList[element].count < count) {
      const difference = count - elementsList[element].count;
      for (var i = 0; i < difference; i++) {
        const weight = elementsList[element].indexes[count - difference + i];
        const newElement = { name: element, weight: weight }
        this.addElement(sortedList, newElement);
      }
    } else if (elementsList[element].count > count) {
      const difference = elementsList[element].count - count;
      for (var i = 0; i < difference; i++) {
        this.removeFromList(sortedList, element);
      }
    }
    elementsList[element].count = count
  }

  // if the list is locked, we add an element based on its book position
  // if unlocked, we just add the new element to the end
  addElement(sortedList, element) {
    if (this.state.locked) {
      const lesser = sortedList.find(e => e.weight > element.weight);
      if (lesser) {
        const lesserIndex = sortedList.indexOf(lesser);
        sortedList.splice(lesserIndex, 0, element);
      } else {
        sortedList.push(element);
      }
    } else {
      sortedList.push(element);
    }
  }

  removeFromList = (list, element) => {
    const index = _.findLastIndex(list, (e => e.name === element));
    list.splice(index, 1);
  }

  rearrangeSortableList = ({oldIndex, newIndex}) => {
    this.setState({
      sortableList: arrayMove(this.state.sortableList, oldIndex, newIndex)
    });
  }

  setConversion = (conversion) => {
    this.setState({ conversion });
  }

  makeSample = () => {
    const config = new SampleConfiguration(
      this.state.sortableList.map(e => e.name),
      this.state.characterStyles,
      this.state.specialCharacters
    );
    this.props.makeSample(this.props.id, this.state.conversion, config.toArray());
  }

  sampleId = () => {
    return this.props.files.find(f => f.type === 'sample').id;
  }

  isAvailable() {
    return this.props.statistics &&
      this.props.statistics.elements &&
      this.props.statistics.elements.length > 0;
  }

  unlock = () => {
    this.setState({ locked: false });
  }

  render() {
    return (
      <Container className='sample'>
        {this.isAvailable() ?
          <Row>
            <Col md='4'>
              <div className="sample-step">
                <div className="sample-step-head">
                  <h2>
                    <span className="sample-num">1.</span>
                    Choose Counts{' '}
                    <HelpPopover
                      size="sm"
                      name="sample-step-1-descrip"
                      placement={'right'}
                      title="Choose Element Counts for the Sample"
                    >
                      <p>
                        This determines how many copies of an element appear in
                        step 2.
                      </p>
                      <p>
                        While the <strong>Rearrange</strong> list is
                        locked, new elements will be added in the order they appear
                        in the book. When unlocked, new elements are added to the
                        bottom of the list and the list can be rearranged. The list
                        can only be unlocked once.
                      </p>
                    </HelpPopover>
                  </h2>
                </div>
                <ElementCountList
                  elements={Object.values(this.state.elements)}
                  updateElementCount={this.updateElementCount}
                  setAllElementCounts={this.setAllElementCounts}
                  selectAllByStyle={this.selectAllByStyle}
                />
              </div>
            </Col>
            <Col md='4'>
              <div className="sample-step">
                <div className="sample-step-head">
                  <h2>
                    <span className="sample-num">2.</span>
                    Rearrange{' '}
                    <HelpPopover
                      size="sm"
                      name="sample-step-2-descrip"
                      placement={'right'}
                      title="Rearrange the Order Elements Appear"
                    >
                      <p>
                        Unlock the list to click and drag elements to determine the
                        order they will appear in the sample.
                      </p>
                      <p>
                        Once unlocked, the elements list cannot be re-locked.
                        This is because the Hub cannot keep adding elements in
                        the order they appear in the book after you have begun
                        rearranging them manually.
                      </p>
                    </HelpPopover>
                  </h2>
                </div>
                <div id="sample-lock-pad">
                  <Button
                    id="sample-lock"
                    onClick={this.unlock}
                    style={{width: '100%'}}
                    disabled={!this.state.locked}
                    className='file-nav'
                    title={this.state.locked ?
                      'unlock element list' :
                      'Cannot be locked again'}
                    >
                      <span className='file-nav-icon'>
                        <i className={`fa fa-${this.state.locked ? 'lock' : 'unlock'}`}/>
                      </span>
                      <span className='file-nav-category'>
                        {this.state.locked ?
                          'Elements are locked in place.' :
                          'Elements can be rearranged.'}
                        </span>
                      </Button>
                    </div>
                    <SortableElements
                      locked={this.state.locked}
                      items={this.state.sortableList.map(e => e.name)}
                      update={this.rearrangeSortableList}
                    />
                  </div>
                </Col>
                <Col md='4'>
                  {this.props.loading ? <div className="loading-overlay">&nbsp;</div> : null }
                  <div className="sample-step">
                    <div className="sample-step-head">
                      <h2>
                        <span className="sample-num">3.</span>
                        Make the Sample{' '}
                        <HelpPopover
                          size="sm"
                          name="sample-step-3-descrip"
                          placement={'left'}
                          title="Choose an Output and Make a Sample"
                        >
                          <p>
                            Choose one of the outputs from the list below and
                            click &ldquo;Make Sample&rdquo; when ready.
                          </p>
                          <p>
                            When your sample is done processing, you will be
                            able to download a zip archive containing sam
                            version of your sample and your chosen output.
                          </p>
                        </HelpPopover>
                      </h2>
                    </div>
                    <ButtonGroup
                      className='sample-conversions'
                      vertical
                    >
                      {availableConversions.map((conversion, index) => (
                        <Button
                          id={`convert-${conversion.slug}`}
                          key={`convert-${conversion.slug}`}
                          onClick={() => this.setConversion(conversion.slug)}
                          active={this.state.conversion === conversion.slug}
                          className={this.state.conversion === conversion.slug ? 'active' : ''}
                          title={`choose ${descriptionForFileType(conversion.output)} output`}
                          size='lg'
                          color='primary'
                          outline
                        >
                          {getFileTypeName(conversion.output)}
                        </Button>
                      ))}
                    </ButtonGroup>
                    <ButtonAction
                      id='sample-convert'
                      className='sample-btn'
                      icon='fa-check'
                      size='lg'
                      color='success'
                      action={this.makeSample}
                      disabled={!this.state.conversion}
                      title='generate the sample'
                    >
                      Make Sample
                    </ButtonAction>
                    <ReactCSSTransition
                      classNames="fade"
                      key="reactfade"
                      timeout={{ enter: 500, exit: 300 }}>
                        <div>
                        {this.state.showSample ?
                          <a
                            className="sample-btn btn btn-primary btn-lg"
                            style={{marginTop: '1rem'}}
                            title="Download Sample"
                            data-turbolinks={false}
                            href={`/get/assets/${this.sampleId()}`}
                          >
                            Download Sample <i className="fa fa-download" />
                          </a> : null}
                        </div>
                      </ReactCSSTransition>
                    </div>
                  </Col>
                </Row> :
          <p style={{textAlign: 'center'}} className="h2 text-muted mt-3">
            Convert project files to sam in order to make a sample.
          </p>
        }
      </Container>
    );
  }
}

function mapStateToProps(state) {
  return {
    id: state.get('id'),
    loading: state.get('loading'),
    statistics: state.get('statistics'),
    files: state.get('files'),
    error: state.get('error'),
  };
}

export const SampleContainer = connect(
  mapStateToProps,
  actionCreators,
)(toJS(Sample));
